import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { LuHexagon, LuMessageSquare, LuPin, LuUser } from 'react-icons/lu';
import { aggLabel, formatMetricValue, OP_LABEL, unitLabel, initials } from './metricTreeApi';

// Owner avatar (initials chip) or the empty user glyph.
function OwnerAvatar({ owner }) {
  if (!owner) return <LuUser size={16} />;
  return (
    <div title={owner} style={{
      width: 22, height: 22, borderRadius: '50%', background: '#EDEBFB', color: '#4f5ed1',
      display: 'grid', placeItems: 'center', fontSize: 10, fontWeight: 800, letterSpacing: '.02em',
    }}>
      {initials(owner)}
    </div>
  );
}

// One WoW / QoQ comparison segment.
function CompareChip({ label, pct }) {
  const has = pct !== undefined && pct !== null && Number.isFinite(Number(pct));
  const up = Number(pct) >= 0;
  return (
    <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px' }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#64748B', letterSpacing: '.02em' }}>{label}</div>
      <div style={{
        marginTop: 2, fontSize: 12.5, fontWeight: 800, fontVariantNumeric: 'tabular-nums',
        color: !has ? '#94A3B8' : up ? '#0E9F6E' : '#E02424',
      }}>
        {!has ? '—' : `${up ? '↑' : '↓'} ${Math.abs(Number(pct)).toFixed(Math.abs(pct) < 100 ? 1 : 0)}%`}
      </div>
    </div>
  );
}

/**
 * MetricNode — a metric card. Two states:
 *  • EMPTY (an unconfigured scaffold node — no event bound): Mixpanel's placeholder
 *    card — hexagon + title, a skeleton value, a "+ Connect Metric" call to action.
 *  • FILLED (event bound / computed, or a formula): metric name + type tag, big
 *    tabular value, weekly sparkline, and a period-over-period Δ% chip.
 *
 * Four connection handles (default Left/Right for left-to-right trees, plus id'd
 * Top/Bottom for the top-down KPI scaffold) so both layouts wire up cleanly.
 */

const FONT = '"Open Sans", system-ui, sans-serif';

// Inject the shimmer keyframes once (plain-div cards, so no Chakra Skeleton).
if (typeof document !== 'undefined' && !document.getElementById('mt-shimmer-anim')) {
  const s = document.createElement('style');
  s.id = 'mt-shimmer-anim';
  s.textContent = '@keyframes mtShimmer{0%{background-position:-320px 0}100%{background-position:320px 0}}@keyframes mtSpin{to{transform:rotate(360deg)}}';
  document.head.appendChild(s);
}

// A shimmering skeleton bar shown while a card's value is computing.
function Shimmer({ w, h, style }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: 6,
      background: 'linear-gradient(90deg,#eef1f6 25%,#e0e6ef 37%,#eef1f6 63%)',
      backgroundSize: '320px 100%', animation: 'mtShimmer 1.15s ease-in-out infinite',
      ...style,
    }} />
  );
}

// Extra handles for top-down scaffolds; kept tiny/subtle so they don't clutter.
function Handles() {
  const side = { width: 8, height: 8, background: '#CBD5E1', border: '2px solid #fff' };
  const tb = { width: 8, height: 8, background: '#CBD5E1', border: '2px solid #fff' };
  return (
    <>
      <Handle type="target" position={Position.Left} style={side} />
      <Handle type="source" position={Position.Right} style={side} />
      <Handle type="source" position={Position.Top} id="t" style={tb} />
      <Handle type="target" position={Position.Bottom} id="b" style={tb} />
    </>
  );
}

// SVG sparkline (area + line + emphasised endpoint) from a numeric series.
function Sparkline({ series, positive }) {
  const pts = (series || []).map((p) => Number(p.v)).filter((n) => Number.isFinite(n));
  const W = 208;
  const H = 34;
  const PAD = 3;
  if (pts.length < 2) {
    return (
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', marginTop: 10 }}>
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="3 3" />
      </svg>
    );
  }
  const min = Math.min(...pts);
  const max = Math.max(...pts);
  const rng = max - min || 1;
  const coords = pts.map((v, i) => {
    const x = PAD + (i * (W - 2 * PAD)) / (pts.length - 1);
    const y = H - PAD - ((v - min) / rng) * (H - 2 * PAD);
    return [x, y];
  });
  const line = coords.map((c, i) => `${i ? 'L' : 'M'}${c[0].toFixed(1)} ${c[1].toFixed(1)}`).join(' ');
  const area = `${line} L${coords[coords.length - 1][0].toFixed(1)} ${H} L${coords[0][0].toFixed(1)} ${H} Z`;
  const stroke = positive ? '#0E9F6E' : '#E02424';
  const fill = positive ? 'rgba(14,159,110,0.12)' : 'rgba(224,36,36,0.10)';
  const end = coords[coords.length - 1];
  return (
    <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block', marginTop: 10 }}>
      <path d={area} fill={fill} />
      <path d={line} fill="none" stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={end[0].toFixed(1)} cy={end[1].toFixed(1)} r="2.6" fill={stroke} />
    </svg>
  );
}

// Footer: owner avatar + working comment + pin controls. Handlers come through
// node.data (injected by the canvas); stopPropagation so they don't select/drag.
function CardFooter({ id, data, padded }) {
  const pinned = !!data?.pinned;
  const count = (data?.comments || []).length;
  const btn = {
    border: 'none', background: 'transparent', cursor: 'pointer', padding: 2,
    display: 'inline-flex', alignItems: 'center', color: 'inherit',
  };
  const stop = (fn) => (e) => { e.stopPropagation(); if (fn) fn(id); };
  return (
    <div style={{
      marginTop: padded ? 0 : 10, paddingTop: padded ? 7 : 9,
      paddingLeft: padded ? 12 : 0, paddingRight: padded ? 12 : 0, paddingBottom: padded ? 7 : 0,
      borderTop: '1px solid #EEF1F6',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#B6C0CE',
    }}>
      <OwnerAvatar owner={data?.owner} />
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <button type="button" title="Comments" aria-label="Comments" style={btn} onClick={stop(data?.onOpenComments)}>
          <LuMessageSquare size={14} color={count ? '#4f5ed1' : '#B6C0CE'} />
          {count > 0 && <span style={{ fontSize: 10, fontWeight: 800, color: '#4f5ed1', marginLeft: 2 }}>{count}</span>}
        </button>
        <button type="button" title={pinned ? 'Unpin' : 'Pin'} aria-label={pinned ? 'Unpin' : 'Pin'}
          aria-pressed={pinned} style={btn} onClick={stop(data?.onTogglePin)}>
          <LuPin size={14} color={pinned ? '#4f5ed1' : '#B6C0CE'} fill={pinned ? '#4f5ed1' : 'none'} />
        </button>
      </div>
    </div>
  );
}

const cardShell = (selected, isRoot) => ({
  position: 'relative',
  width: 230,
  background: '#fff',
  border: `1px solid ${selected ? '#4f5ed1' : '#E3E9F1'}`,
  borderLeft: isRoot ? '4px solid #4f5ed1' : `1px solid ${selected ? '#4f5ed1' : '#E3E9F1'}`,
  borderRadius: 14,
  boxShadow: selected
    ? '0 0 0 2px rgba(79,94,209,0.22), 0 10px 28px rgba(15,26,43,0.12)'
    : '0 1px 2px rgba(15,26,43,0.06), 0 6px 18px rgba(15,26,43,0.06)',
  fontFamily: FONT,
});

// Empty placeholder card — Mixpanel's "Connect Metric" state.
function EmptyCard({ id, data, selected }) {
  const bar = (w) => <div style={{ width: w, height: 6, borderRadius: 3, background: '#E7ECF2' }} />;
  return (
    <div style={{ ...cardShell(selected, data?.isRoot) }}>
      <Handles />
      <div style={{ padding: '12px 14px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <LuHexagon size={14} color="#64748B" />
          <span style={{ fontSize: 12.5, fontWeight: 700, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {data?.label || 'Metric'}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '20px 0 18px' }}>
          <div style={{ display: 'flex', gap: 6 }}>{bar(26)}{bar(26)}{bar(26)}</div>
          <div style={{ display: 'flex', gap: 6 }}>{bar(18)}{bar(18)}</div>
        </div>
      </div>
      <div style={{ borderTop: '1px solid #EEF1F6', padding: '10px 0', textAlign: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#64748B' }}>+ Connect Metric</span>
      </div>
      <CardFooter id={id} data={data} padded />
    </div>
  );
}

function MetricNode({ id, data, selected }) {
  const kind = data?.kind === 'formula' ? 'formula' : 'metric';
  const metric = data?.metric || {};
  const formula = data?.formula || {};
  const hasValue = data?.value !== undefined && data?.value !== null;

  // MT-09: "configured" must test the events COLLECTION, not just the legacy
  // single-event scalar — a multi-event union sets metric.event=null and fills
  // metric.events[], which previously reverted the card to the empty scaffold.
  const isConfigured = !!metric.event || (Array.isArray(metric.events) && metric.events.length > 0);
  if (kind !== 'formula' && !isConfigured && !hasValue) {
    return <EmptyCard id={id} data={data} selected={selected} />;
  }

  const up = Number(data?.delta_pct) >= 0;
  // Show a shimmer while the value is being computed (not yet arrived).
  const loading = !!data?.computing && !hasValue;

  const tag = kind === 'formula'
    ? `formula · ${OP_LABEL[formula.operator] || formula.operator || ''}`
    : aggLabel(metric.agg);

  const unit = unitLabel(data);

  return (
    <div style={{
      ...cardShell(selected, data?.isRoot), padding: '13px 15px',
      ...(data?.pinned ? { boxShadow: '0 0 0 2px rgba(79,94,209,0.35), 0 8px 22px rgba(15,26,43,0.12)' } : {}),
    }}>
      <Handles />

      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <LuHexagon size={13} color="#4f5ed1" />
        <span style={{
          fontSize: 11.5, fontWeight: 700, color: '#334155',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1,
        }}>
          {data?.label || 'Untitled metric'}
        </span>
        <span style={{
          fontSize: 9.5, fontWeight: 700, color: '#64748B', background: '#EEF2F7',
          padding: '2px 6px', borderRadius: 5, letterSpacing: '.02em', whiteSpace: 'nowrap',
        }}>
          {tag}
        </span>
      </div>

      {loading ? (
        <>
          <Shimmer w="66%" h={26} style={{ marginTop: 12 }} />
          <Shimmer w="34%" h={10} style={{ marginTop: 9 }} />
          <Shimmer w="100%" h={34} style={{ marginTop: 12 }} />
          <div style={{
            marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            border: '1px solid #EEF1F6', borderRadius: 10, padding: '9px 0',
          }}>
            <span style={{
              width: 12, height: 12, borderRadius: '50%', border: '2px solid #E3E9F1', borderTopColor: '#94A3B8',
              display: 'inline-block', animation: 'mtSpin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: 11.5, fontWeight: 600, color: '#94A3B8' }}>Loading…</span>
          </div>
        </>
      ) : (
        <>
          <div style={{
            marginTop: 10, fontSize: 26, fontWeight: 800, letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums', lineHeight: 1, color: hasValue ? '#0F1A2B' : '#CBD5E0',
          }} title={hasValue ? String(data.value) : 'Not computed yet'}>
            {hasValue ? formatMetricValue(data.value) : '—'}
          </div>
          {unit ? <div style={{ marginTop: 4, fontSize: 11.5, color: '#94A3B8', fontWeight: 500 }}>{unit}</div> : null}

          <Sparkline series={data?.series} positive={up} />

          {/* WoW / QoQ comparison pill */}
          <div style={{
            marginTop: 10, display: 'flex', border: '1px solid #EEF1F6', borderRadius: 10, overflow: 'hidden',
          }}>
            <CompareChip label="WoW" pct={data?.wow_pct} />
            <div style={{ width: 1, background: '#EEF1F6' }} />
            <CompareChip label="QoQ" pct={data?.qoq_pct} />
          </div>
        </>
      )}

      <CardFooter id={id} data={data} />
    </div>
  );
}

export default memo(MetricNode);

export const nodeTypes = { metricNode: MetricNode };
